import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AuthPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-full flex relative overflow-hidden" data-testid="auth-page">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full bg-indigo-600/10 blur-[140px] pointer-events-none" />

      <div className="hidden lg:flex flex-col justify-between w-[46%] p-14 relative">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center glow-ring"><Layers className="w-5 h-5 text-white" /></div>
          <span className="font-display text-xl font-semibold text-slate-100">OmniModel</span>
        </div>
        <div className="rise">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-4">Every frontier model, one workspace</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-100 leading-[1.05]">
            Ask anything.<br />We route it to the<br /><span className="text-indigo-400">right mind.</span>
          </h1>
          <p className="mt-6 text-base text-slate-400 max-w-md leading-relaxed">
            Pick the kind of inquiry you have. OmniModel recommends the strongest providers and models for the job — GPT, Claude, Gemini — then lets you switch mid-conversation.
          </p>
        </div>
        <div className="flex gap-6 text-xs text-slate-500">
          <span><span className="text-emerald-400">●</span> OpenAI</span>
          <span><span className="text-amber-400">●</span> Anthropic</span>
          <span><span className="text-cyan-400">●</span> Google Gemini</span>
          <span><span className="text-purple-400">●</span> Kimi</span>
          <span><span className="text-sky-400">●</span> DeepSeek</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative">
        <form onSubmit={submit} className="w-full max-w-md bg-[#11131E]/90 backdrop-blur-xl border border-indigo-500/20 shadow-2xl rounded-2xl p-8 rise" data-testid="auth-form">
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center"><Layers className="w-4 h-4 text-white" /></div>
            <span className="font-display font-semibold text-slate-100">OmniModel</span>
          </div>

          <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-100 mb-1">Welcome back</h2>
          <p className="text-sm text-slate-400 mb-6">Sign in to continue your conversations.</p>

          <div className="space-y-4">
            <Field label="Email"><input data-testid="auth-email-input" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@example.com" required /></Field>
            <Field label="Password"><input data-testid="auth-password-input" type="password" className={inputCls} value={form.password} onChange={update("password")} placeholder="••••••••" minLength={6} required /></Field>
          </div>

          {error && <p data-testid="auth-error" className="mt-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}

          <div className="mt-6 flex items-center justify-between">
            <button data-testid="auth-submit-btn" disabled={busy} type="submit" className="w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-xl transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
          <Link to="/forgot-password" data-testid="forgot-password-link" className="mt-4 block text-center text-xs text-slate-400 hover:text-indigo-300 transition-colors">Forgot your password?</Link>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#0D0F18] border border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 outline-none rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</span>
    {children}
  </label>
);
